import * as i0 from '@angular/core';
import { input, inject, PLATFORM_ID, ElementRef, Renderer2, Directive, InjectionToken, makeEnvironmentProviders, computed, Component, NgModule } from '@angular/core';
import { THEME_ICONS } from '@virginmediao2/momentum-angular/tokens';
import { isPlatformBrowser } from '@angular/common';

class IconRendererDirective {
    constructor() {
        this.icon = input.required({ alias: 'voIconRenderer' });
        this.#platformId = inject(PLATFORM_ID);
        this.#elRef = inject((ElementRef));
        this.#renderer = inject(Renderer2);
    }
    #platformId;
    #elRef;
    #renderer;
    generateSvgElement(svg, child) {
        const svgPath = this.#renderer.createElement(child.element, 'svg');
        Object.entries(child)
            .filter(([key]) => key !== 'element')
            .forEach(([key, value]) => {
            if (key === 'children' && Array.isArray(value)) {
                return value.forEach((c) => this.generateSvgElement(svgPath, c));
            }
            this.#renderer.setAttribute(svgPath, key, value);
        });
        this.#renderer.appendChild(svg, svgPath);
    }
    ngOnChanges() {
        const icon = this.icon();
        if (isPlatformBrowser(this.#platformId)) {
            const childElements = this.#elRef.nativeElement.children;
            for (const child of childElements) {
                this.#renderer.removeChild(this.#elRef.nativeElement, child);
            }
        }
        this.generateSvgElement(this.#elRef.nativeElement, icon);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: IconRendererDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "17.3.4", type: IconRendererDirective, isStandalone: true, selector: "[voIconRenderer]", inputs: { icon: { classPropertyName: "icon", publicName: "voIconRenderer", isSignal: true, isRequired: true, transformFunction: null } }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: IconRendererDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[voIconRenderer]',
                    standalone: true,
                }]
        }] });

const ICONS = new InjectionToken('momentum-icons');
const provideIcons = (config) => makeEnvironmentProviders([
    {
        provide: ICONS,
        useValue: new Map(Object.entries(config)),
    },
]);

const ICON_COMPONENT = new InjectionToken('IconComponent');

const storyblokDomain = 'https://a.storyblok.com';
const storyblokAltDomain = 'https://a2.storyblok.com';
class IconComponent {
    constructor() {
        this.#icons = inject(ICONS, { optional: true }) || new Map();
        this.#themeIcons = inject(THEME_ICONS, { optional: true }) || {};
        this._ariaHidden = computed(() => {
            const ariaLabel = this.ariaLabel();
            const ariaHidden = this.ariaHidden();
            if (ariaHidden) {
                return ariaHidden;
            }
            if (ariaLabel) {
                return null;
            }
            return true;
        });
        this._iconType = computed(() => {
            const icon = this.icon();
            if (typeof icon === 'object') {
                return 'icon';
            }
            if (icon.startsWith('https://')) {
                return 'url';
            }
            return 'string';
        });
        this._iconName = computed(() => {
            try {
                const icon = this._icon();
                return icon ? icon['data-name'] : null;
            }
            catch (e) {
                return null;
            }
        });
        this._icon = computed(() => {
            const iconType = this._iconType();
            const providedIcons = {
                ...Object.fromEntries(this.#icons.entries()),
                ...this.#themeIcons,
            };
            if (iconType === 'icon') {
                return this.icon();
            }
            if (iconType === 'string') {
                const iconName = this.icon();
                const icon = providedIcons[iconName];
                if (icon) {
                    return icon;
                }
                console.error(`Icon ${this.icon} not found, perhaps it was not provided?`);
            }
            return null;
        });
        this._iconUrl = computed(() => {
            const iconType = this._iconType();
            const icon = this.icon();
            if (iconType === 'url') {
                const i = icon;
                const path = i.startsWith(storyblokDomain) ? i.replace(storyblokDomain, storyblokAltDomain) : i;
                return `url(${path})`;
            }
            return null;
        });
        this.ariaLabel = input(null, { alias: 'aria-label' });
        /** Hide from accessibility tree */
        this.ariaHidden = input(null, { alias: 'aria-hidden' });
        /** Sets the icon size */
        this.size = input('md');
        /** Sets the icon name */
        this.icon = input.required();
        /** Sets the icon color */
        this.color = input();
        /** Sets the icon ratio */
        this.iconRatio = input('0.75');
    }
    #icons;
    #themeIcons;
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: IconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.4", type: IconComponent, isStandalone: true, selector: "vo-icon", inputs: { ariaLabel: { classPropertyName: "ariaLabel", publicName: "aria-label", isSignal: true, isRequired: false, transformFunction: null }, ariaHidden: { classPropertyName: "ariaHidden", publicName: "aria-hidden", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, icon: { classPropertyName: "icon", publicName: "icon", isSignal: true, isRequired: true, transformFunction: null }, color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, iconRatio: { classPropertyName: "iconRatio", publicName: "iconRatio", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class.icon": "true", "class.icon--color-success": "color() === \"success\"", "class.icon--color-error": "color() === \"error\"", "class.icon--color-warning": "color() === \"warning\"", "class.icon--color-info": "color() === \"info\"", "class.icon--size-sm": "size() === \"sm\"", "class.icon--size-md": "size() === \"md\"", "class.icon--size-lg": "size() === \"lg\"", "class.icon--size-xl": "size() === \"xl\"", "class.icon--size-2xl": "size() === \"2xl\"", "class.icon--size-3xl": "size() === \"3xl\"", "class.icon--ratio-1": "iconRatio() === \"1\"", "class.icon--ratio-05": "iconRatio() === \"0.5\"", "class.icon--ratio-075": "iconRatio() === \"0.75\"", "class.icon--size-inherit": "size() === \"inherit\"", "attr.aria-label": "ariaLabel()", "attr.aria-hidden": "_ariaHidden()", "attr.role": "ariaLabel() ? \"presentation\" : null", "attr.data-icon-name": "_iconName()" } }, providers: [
            {
                provide: ICON_COMPONENT,
                useClass: IconComponent,
            },
        ], ngImport: i0, template: "@if (_icon(); as icon) {\n    <span class=\"icon__host\" [voIconRenderer]=\"icon\"></span>\n}\n\n@if (_iconUrl(); as icon) {\n    <span class=\"icon__host\">\n        <span class=\"icon__mask\"\n              [style.--_maskurl]=\"icon\"></span>\n    </span>\n}\n", styles: [":host(.icon){--icon-size:1em;display:inline-flex;justify-content:center;align-items:center;aspect-ratio:1;box-sizing:content-box;vertical-align:middle;width:1em;width:var(--icon-size);height:1em;height:var(--icon-size);font-size:calc(1em * var(--icon-ratio));font-size:calc(var(--icon-size) * var(--icon-ratio))}:host(.icon--ratio-1){--icon-ratio:1}:host(.icon--ratio-075){--icon-ratio:.75}:host(.icon--ratio-05){--icon-ratio:.5}:host(.icon--color-success){color:var(--color-success-bg)}:host(.icon--color-error){color:var(--color-error-bg-dim)}:host(.icon--color-warning){color:var(--color-warning-bg)}:host(.icon--color-info){color:var(--color-info-bg)}:host(.icon--size-sm){--icon-size:1rem}:host(.icon--size-md){--icon-size:1.5rem}:host(.icon--size-lg){--icon-size:2rem}:host(.icon--size-xl){--icon-size:3rem}:host(.icon--size-2xl){--icon-size:4rem}:host(.icon--size-3xl){--icon-size:8rem}:host(.icon--size-inherit){--icon-size:1em}:host(.icon) ::ng-deep svg{max-height:1em;fill:currentColor;padding:0;height:100%}.icon__host{display:flex;height:100%;width:100%;align-items:center;justify-items:center;place-items:center;align-content:center;justify-content:center;place-content:center}.icon__mask{display:flex;vertical-align:middle;width:100%;height:calc(100% * var(--icon-ratio));background:currentColor;-webkit-mask-image:var(--_maskurl);mask-image:var(--_maskurl);-webkit-mask-size:contain;mask-size:contain;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-position:center center;mask-position:center center}\n"], dependencies: [{ kind: "directive", type: IconRendererDirective, selector: "[voIconRenderer]", inputs: ["voIconRenderer"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: IconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vo-icon', standalone: true, imports: [IconRendererDirective], providers: [
                        {
                            provide: ICON_COMPONENT,
                            useClass: IconComponent,
                        },
                    ], host: {
                        '[class.icon]': 'true',
                        '[class.icon--color-success]': 'color() === "success"',
                        '[class.icon--color-error]': 'color() === "error"',
                        '[class.icon--color-warning]': 'color() === "warning"',
                        '[class.icon--color-info]': 'color() === "info"',
                        '[class.icon--size-sm]': 'size() === "sm"',
                        '[class.icon--size-md]': 'size() === "md"',
                        '[class.icon--size-lg]': 'size() === "lg"',
                        '[class.icon--size-xl]': 'size() === "xl"',
                        '[class.icon--size-2xl]': 'size() === "2xl"',
                        '[class.icon--size-3xl]': 'size() === "3xl"',
                        '[class.icon--ratio-1]': 'iconRatio() === "1"',
                        '[class.icon--ratio-05]': 'iconRatio() === "0.5"',
                        '[class.icon--ratio-075]': 'iconRatio() === "0.75"',
                        '[class.icon--size-inherit]': 'size() === "inherit"',
                        '[attr.aria-label]': 'ariaLabel()',
                        '[attr.aria-hidden]': '_ariaHidden()',
                        '[attr.role]': 'ariaLabel() ? "presentation" : null',
                        '[attr.data-icon-name]': '_iconName()',
                    }, template: "@if (_icon(); as icon) {\n    <span class=\"icon__host\" [voIconRenderer]=\"icon\"></span>\n}\n\n@if (_iconUrl(); as icon) {\n    <span class=\"icon__host\">\n        <span class=\"icon__mask\"\n              [style.--_maskurl]=\"icon\"></span>\n    </span>\n}\n", styles: [":host(.icon){--icon-size:1em;display:inline-flex;justify-content:center;align-items:center;aspect-ratio:1;box-sizing:content-box;vertical-align:middle;width:1em;width:var(--icon-size);height:1em;height:var(--icon-size);font-size:calc(1em * var(--icon-ratio));font-size:calc(var(--icon-size) * var(--icon-ratio))}:host(.icon--ratio-1){--icon-ratio:1}:host(.icon--ratio-075){--icon-ratio:.75}:host(.icon--ratio-05){--icon-ratio:.5}:host(.icon--color-success){color:var(--color-success-bg)}:host(.icon--color-error){color:var(--color-error-bg-dim)}:host(.icon--color-warning){color:var(--color-warning-bg)}:host(.icon--color-info){color:var(--color-info-bg)}:host(.icon--size-sm){--icon-size:1rem}:host(.icon--size-md){--icon-size:1.5rem}:host(.icon--size-lg){--icon-size:2rem}:host(.icon--size-xl){--icon-size:3rem}:host(.icon--size-2xl){--icon-size:4rem}:host(.icon--size-3xl){--icon-size:8rem}:host(.icon--size-inherit){--icon-size:1em}:host(.icon) ::ng-deep svg{max-height:1em;fill:currentColor;padding:0;height:100%}.icon__host{display:flex;height:100%;width:100%;align-items:center;justify-items:center;place-items:center;align-content:center;justify-content:center;place-content:center}.icon__mask{display:flex;vertical-align:middle;width:100%;height:calc(100% * var(--icon-ratio));background:currentColor;-webkit-mask-image:var(--_maskurl);mask-image:var(--_maskurl);-webkit-mask-size:contain;mask-size:contain;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-position:center center;mask-position:center center}\n"] }]
        }] });

class IconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: IconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.4", ngImport: i0, type: IconModule, imports: [IconComponent], exports: [IconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: IconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.4", ngImport: i0, type: IconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [IconComponent],
                    exports: [IconComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ICONS, ICON_COMPONENT, IconComponent, IconModule, IconRendererDirective, provideIcons };
